import { CommonModule } from '@angular/common';
import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import { BillEorHeaderResponse } from '../../../../models/api/view/claim/financials/bills/bill-eor-header-response';
import { ClaimBillDetail } from '../../../../models/api/view/claim/financials/bills/claim-bill-detail';
import { EorDataMapper } from '../../../../utilities/eor-data-mapper';
import { AddressLabelComponent } from '../../../core/labels/address-label.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';

@Component({
  selector: 'app-eor-detail-section',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    DetailPanelComponent,
    DetailItemComponent,
    AddressLabelComponent,
  ],
  exportAs: 'eorDetailSection',
  template: `
    <div class="tw-flex tw-basis-full tw-gap-4 tw-pb-4">
      <app-card
        label="Bill/Invoice detail"
        class="tw-grow"
        [expanded]="true"
        [showExpand]="true"
        theme="Light"
      >
        <div class="tw-flex tw-gap-8">
          <div class="tw-basis-1/3">
            <app-card
              label="Payor"
              class="tw-grow"
              [expanded]="true"
              [showExpand]="false"
            >
              @if (eorPayorData) {
                <app-detail-panel [dataSource]="eorPayorData">
                  <app-detail-item label="Payor" name="payor" />
                  <app-detail-item
                    label="TPA claim number"
                    name="tpaClaimNum"
                    labelCase="none"
                  />
                  <app-detail-item
                    label="Bill internal control number"
                    name="billIcn"
                  />
                  <app-detail-item
                    label="Bill DCN"
                    name="billDcn"
                    labelCase="none"
                  />
                  <app-detail-item
                    label="SIR DCN"
                    name="sirDcn"
                    labelCase="none"
                  />
                  <app-detail-item label="Service date" name="serviceDate" />
                  <app-detail-item
                    label="Received by vendor"
                    name="vendorRcvdDate"
                    format="d"
                  />
                  <app-detail-item
                    label="Processed by vendor"
                    name="vendorProcDate"
                    format="d"
                  />
                  <app-detail-item
                    label="Date of injury"
                    name="injuryDate"
                    format="d"
                  />
                  <app-detail-item
                    label="ICD codes"
                    name="icdCodes"
                    labelCase="none"
                  />
                </app-detail-panel>
              }
            </app-card>
          </div>
          <div class="tw-basis-1/3">
            <div class="tw-flex tw-flex-col tw-gap-3">
              <app-card
                label="Injured"
                class="tw-grow tw-pb-[20px]"
                [expanded]="true"
                [showExpand]="false"
              >
                @if (eorInjuredData) {
                  <app-detail-panel [dataSource]="eorInjuredData">
                    <app-detail-item label="Injured name" name="fullName" />
                    <app-detail-item label="SSN" name="ssn" labelCase="none" />
                    <app-detail-item
                      label="Patient account number"
                      name="patientAccount"
                    />
                    <app-detail-item label="Phone" name="phone" format="p" />
                    <app-detail-item label="Address" name="address">
                      <ng-template let-data #value>
                        <app-address-label
                          [street1]="data.address1"
                          [street2]="data.address2"
                          [city]="data.city"
                          [state]="data.state"
                          [postalCode]="data.zip"
                        />
                      </ng-template>
                    </app-detail-item>
                  </app-detail-panel>
                }
              </app-card>
              <app-card
                label="Employer"
                class="tw-grow"
                [expanded]="true"
                [showExpand]="false"
              >
                @if (eorEmployerData) {
                  <app-detail-panel [dataSource]="eorEmployerData">
                    <app-detail-item label="Employer" name="employer" />
                    <app-detail-item
                      label="Contract number"
                      name="contractNumber"
                    />
                    <app-detail-item label="Address" name="address">
                      <ng-template let-data #value>
                        <app-address-label
                          [street1]="data.address1"
                          [street2]="data.address2"
                          [city]="data.city"
                          [state]="data.state"
                          [postalCode]="data.zip"
                        />
                      </ng-template>
                    </app-detail-item>
                  </app-detail-panel>
                }
              </app-card>
            </div>
          </div>
          <div class="tw-basis-1/3">
            <div class="tw-flex tw-flex-col tw-gap-3">
              <app-card
                label="Carrier"
                class="tw-grow tw-pb-[20px]"
                [expanded]="true"
                [showExpand]="false"
              >
                @if (eorCarrierData) {
                  <app-detail-panel [dataSource]="eorCarrierData">
                    <app-detail-item label="Carrier" name="carrierName" />
                    <app-detail-item
                      label="Phone"
                      name="carrierPhone"
                      format="p"
                    />
                    <app-detail-item label="Address" name="address">
                      <ng-template let-data #value>
                        <app-address-label
                          [street1]="data.address1"
                          [street2]="data.address2"
                          [city]="data.city"
                          [state]="data.state"
                          [postalCode]="data.zip"
                        />
                      </ng-template>
                    </app-detail-item>
                  </app-detail-panel>
                }
              </app-card>
              <app-card
                label="Provider"
                class="tw-grow"
                [expanded]="true"
                [showExpand]="false"
              >
                @if (eorProviderData) {
                  <app-detail-panel [dataSource]="eorProviderData">
                    <app-detail-item label="Provider" name="provider" />
                    <app-detail-item
                      label="Treating provider"
                      name="treatingProvider"
                    />
                    <app-detail-item
                      label="Tax ID"
                      name="provTaxId"
                      labelCase="none"
                    />
                    <app-detail-item
                      label="National provider identifier"
                      name="provNpi"
                      labelCase="none"
                    />
                    <app-detail-item
                      label="Phone"
                      name="provPhone"
                      format="p"
                    />
                    <app-detail-item label="Address" name="address">
                      <ng-template let-data #value>
                        <app-address-label
                          [street1]="data.address1"
                          [street2]="data.address2"
                          [city]="data.city"
                          [state]="data.state"
                          [postalCode]="data.zip"
                        />
                      </ng-template>
                    </app-detail-item>
                  </app-detail-panel>
                }
              </app-card>
            </div>
          </div>
        </div>
      </app-card>
    </div>
  `,
})
export class EorDetailSectionComponent {
  @Input() billItem: ClaimBillDetail | null = null;
  @Input() billEorHeader: BillEorHeaderResponse | null = null;
  @Input() claimNumber = '';

  @ViewChildren(CardComponent) cards!: QueryList<CardComponent>;

  toggleCards(expanded: boolean) {
    if (this.cards) {
      this.cards.forEach((card) => {
        card.expanded = expanded;
      });
    }
  }

  get eorPayorData() {
    return EorDataMapper.mapPayorData(
      this.billEorHeader,
      this.billItem,
      this.claimNumber,
    );
  }

  get eorInjuredData() {
    return EorDataMapper.mapInjuredData(this.billEorHeader);
  }

  get eorEmployerData() {
    return EorDataMapper.mapEmployerData(this.billEorHeader, this.billItem);
  }

  get eorCarrierData() {
    return EorDataMapper.mapCarrierData(this.billEorHeader);
  }

  get eorProviderData() {
    return EorDataMapper.mapProviderData(this.billEorHeader, this.billItem);
  }
}
